import React from 'react';
import { FormGroup } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import RadioButton from 'v2/form/RadioButton/RadioButton';
import * as yup from 'yup';
import { WebNotificationsSettings } from 'v2/common/WebNotificationsSettings/WebNotificationsSettings';
import { TranslationKeys } from 'translations';
import { useTranslation } from 'react-i18next';

export const NotificationSettingsSchema = yup.object({
  notifyCompany: yup.boolean(),
  notifyUser: yup.boolean(),
  notifyNewForReviewSearch: yup.boolean(),
  notifyNewInvoice: yup.boolean(),
  notifyNewProviderResponseForReviewedRequests: yup.boolean(),
});

export type TNotificationSettingsFormValues = {
  id: number;
  emailOrg: 'no' | 'yes';
  emailUser: 'no' | 'yes';
  emailNewForReviewSearches: 'no' | 'yes';
  emailNewInvoice: 'no' | 'yes';
  emailNewProviderResponseForReviewedRequests: 'no' | 'yes';
};

export interface INotificationSettingsProps {
  /*
   * @default false
   */
  disabled?: boolean;
  /*
   * @default true
   */
  showEmailUser?: boolean;
  /*
   * @default true
   */
  showEmailOrg?: boolean;
  /*
   * @default true
   */
  showEmailNewInvoice?: boolean;
  /*
   * @default true
   */
  showEmailNewForReviewSearches?: boolean;
  /*
   * @default true
   */
  showEmailNewProviderResponseForReviewedRequests?: boolean;
  /*
   * @default false
   */
  showWebNotifications?: boolean;
  /**
   * prepend this to each registered form field
   * @default ""
   */
  namespacePrefix?: string;
}

export const NotificationSettingsForm = ({
  disabled = false,
  showEmailUser = true,
  showEmailOrg = true,
  showEmailNewInvoice = true,
  showEmailNewForReviewSearches = true,
  showEmailNewProviderResponseForReviewedRequests = true,
  showWebNotifications = false,
  namespacePrefix = '',
}: INotificationSettingsProps) => {
  const { t } = useTranslation();
  const { register } = useFormContext();

  const prefix = namespacePrefix ? `${namespacePrefix}.` : '';

  return (
    <>
      {showEmailOrg && (
        <FormGroup className="mt-4">
          <fieldset id={`${prefix}emailOrg`}>
            <RadioButton
              data-testid="emailOrg"
              label={`Email notifications for ${t(
                TranslationKeys.company_one
              )} level alerts`}
              register={register(`${prefix}emailOrg`, {
                required: false,
              })}
              disabled={disabled}
              options={[
                { label: 'No', value: 'no' },
                { label: 'Yes', value: 'yes' },
              ]}
            />
          </fieldset>
        </FormGroup>
      )}

      {showEmailUser && (
        <FormGroup>
          <fieldset id={`${prefix}emailUser`}>
            <RadioButton
              data-testid="emailUser"
              label="Email notifications for user level alerts"
              register={register(`${prefix}emailUser`, {
                required: false,
              })}
              disabled={disabled}
              options={[
                { label: 'No', value: 'no' },
                { label: 'Yes', value: 'yes' },
              ]}
            />
          </fieldset>
        </FormGroup>
      )}
      {showEmailNewForReviewSearches && (
        <FormGroup>
          <fieldset id={`${prefix}emailNewForReviewSearches`}>
            <RadioButton
              data-testid="emailNewForReviewSearches"
              label="Email notifications for new requests requiring a review"
              register={register(`${prefix}emailNewForReviewSearches`, {
                required: false,
              })}
              disabled={disabled}
              options={[
                { label: 'No', value: 'no' },
                { label: 'Yes', value: 'yes' },
              ]}
            />
          </fieldset>
        </FormGroup>
      )}
      {showEmailNewProviderResponseForReviewedRequests && (
        <FormGroup>
          <fieldset id={`${prefix}emailNewProviderResponseForReviewedRequests`}>
            <RadioButton
              data-testid="emailNewProviderResponseForReviewedRequests"
              label={`Email notifications for ${t(
                TranslationKeys.dataProvider_one
              )} responses to requests you have reviewed`}
              register={register(
                `${prefix}emailNewProviderResponseForReviewedRequests`,
                {
                  required: false,
                }
              )}
              disabled={disabled}
              options={[
                { label: 'No', value: 'no' },
                { label: 'Yes', value: 'yes' },
              ]}
            />
          </fieldset>
        </FormGroup>
      )}

      {showEmailNewInvoice && (
        <FormGroup className="mb-5">
          <fieldset id={`${prefix}emailNewInvoice`}>
            <RadioButton
              data-testid="emailNewInvoice"
              label="Email notification for new invoices"
              register={register(`${prefix}emailNewInvoice`, {
                required: false,
              })}
              disabled={disabled}
              options={[
                { label: 'No', value: 'no' },
                { label: 'Yes', value: 'yes' },
              ]}
            />
          </fieldset>
        </FormGroup>
      )}
      {showWebNotifications && <WebNotificationsSettings disabled={disabled} />}
    </>
  );
};
